import React, { useState, Fragment, useEffect, useRef } from 'react'
import { Button, Input } from '../../atoms'
import isDeepEqual from 'fast-deep-equal/react'
import InputAlert from '../helpers/input-alert'
import { addressEditFields } from './validate'

const AddressFormComponent = ({
  id,
  lookup,
  onChange,
  errors,
  formVariant,
  defaultAddressView = 'postcode',
  defaultFormValues = {},
  soleTrader,
  postcode,
  homeAddress,
  name,
}) => {
  const [formValue, setFormValue] = useState(defaultFormValues)

  const formRef = useRef(formValue)

  if (!isDeepEqual(formRef.current, formValue)) {
    formRef.current = formValue
  }

  useEffect(() => {
    onChange({ ...formValue })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRef.current])

  useEffect(() => {
    setFormValue(defaultFormValues)
  }, [JSON.stringify(defaultFormValues)])

  return (
    <AddressLookup
      key={id}
      data={formValue}
      id={id}
      lookup={lookup}
      setData={setFormValue}
      errors={errors}
      formVariant={formVariant}
      defaultAddressView={defaultAddressView}
      soleTrader={soleTrader}
      postcode={postcode}
      homeAddress={homeAddress}
      name={name}
    />
  )
}

const AddressLookup = ({
  setData,
  data,
  lookup,
  errors,
  formVariant,
  defaultAddressView,
  soleTrader,
  postcode: postcodeProp,
  homeAddress,
  name,
}) => {
  const variantPath = (element) => `elements.form.variants.${formVariant}.${element}`
  const [postcode, setPostcode] = useState('')
  const [addresses, setAddresses] = useState([])
  const [originalSoleTrader, setOriginalSoleTrader] = useState(soleTrader)
  // postcode | static | edit
  const [addressView, setAddressView] = useState(defaultAddressView)
  const [editMode, setEditMode] = useState(false)
  const [postcodeVal, setPostcodeVal] = useState(postcodeProp)
  const [postcodeNotFound, setPostcodeNotFound] = useState(false)
  // const [needToFixErrors, setNeedToFixErrors] = useState(false)

  useEffect(() => {
    if (postcodeProp) {
      lookup(postcodeProp, setAddresses)
    }
  }, [postcodeProp])

  useEffect(() => {
    if (soleTrader !== originalSoleTrader) {
      setPostcode('')
      setData({})
      setAddresses([])
      setAddressView(soleTrader ? 'postcode' : 'static')
      setOriginalSoleTrader(soleTrader)
    }
  }, [soleTrader])

  const returnData = (key, returnValue) => {
    const newData = addressEditFields.reduce((o, { name }) => {
      return {
        ...o,
        [name]: name === key ? returnValue : data[name],
      }
    }, {})
    newData.summaryline = [
      !homeAddress && newData.organisation,
      (newData.number ? `${newData.number} ` : '') + newData.street,
      newData.posttown,
      newData.postcode,
    ]
      .filter(Boolean)
      .join(', ')
    setData(newData)
    setEditMode(true)
  }
  const onAddressSelectionChanged = (e) => {
    if (e.target.value !== 'not-listed') {
      const newData = addresses.find((a) => a.summaryline === e.target.value)
      setData(newData)
      setAddressView('static')
      setEditMode(false)
    } else {
      setPostcodeVal(null)
      setAddressView('edit')
      setEditMode(true)
    }
  }

  const handleViewChange = (view) => {
    if (view === 'postcode') {
      setPostcode('')
      setData({})
      setAddresses([])
    }
    if (view === 'edit') {
      setEditMode(true)
    }
    setAddressView(view)
  }

  const postCodeInput = useRef(null)
  // todo check for other fields being updated
  const postcodeError =
    errors &&
    ((data.buildingname === '' && data.number === '') ||
      data.posttown === '' ||
      data.postcode === '' ||
      data.country === '' ||
      postcode === '')
  const postcodeView = (
    <div>
      {!postcodeVal && <label htmlFor='postcode'>Please enter your postcode</label>}
      {!postcodeVal && (
        <div>
          <div>
            <Input
              type='text'
              value={postcode}
              onChange={(e) => {
                setPostcodeNotFound(false)
                setPostcode(e.target.value)
              }}
              ref={postCodeInput}
              name='postcode'
              error={postcodeError}
            />
          </div>
          {postcodeNotFound && (
            <div className='SMEform__error'>
              We couldn&apos;t find any results for that postcode
            </div>
          )}
          <Button
            onClick={() => {
              const postCode = postCodeInput.current.value
              setPostcode(postCode)
              setPostcodeNotFound(false)
              lookup(postCodeInput.current.value, setAddresses, setPostcodeNotFound)
            }}
            size='sm'
            variant='tertiary'
            style={{
              marginTop: '10px',
              marginBottom: '16px',
            }}
          >
            Find address
          </Button>
        </div>
      )}

      <If condition={addresses.length > 0}>
        <div className='postcodeView'>
          <select name='addresslist' onChange={onAddressSelectionChanged} value={''}>
            {[
              { label: 'Please select your address' },
              { label: "My address isn't listed", value: 'not-listed' },
              ...addresses.map((address) => {
                return { label: address.summaryline, value: address.summaryline }
              }),
            ].map((option, index) => {
              return (
                <option key={index} value={option.value ?? ''}>
                  {option.label}
                </option>
              )
            })}
          </select>
        </div>
      </If>

      <If condition={postcodeError && addresses.length > 0}>
        <InputAlert type='alert' sx={{ pl: '0', pr: '0', variant: variantPath('input.error') }}>
          Please select your address
        </InputAlert>
      </If>
    </div>
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const staticView = (
    <div className='staticView'>
      <Button
        variant='tertiary'
        size='sm'
        onClick={() => {
          setAddresses([])
          handleViewChange('postcode')
          setPostcodeVal(null)
        }}
        style={{ marginBottom: '8px' }}
      >
        Back to address finder
      </Button>
      <div className='addressLines'>
        <div>{data.organisation}</div>
        <div>{data.subbuildingname}</div>
        <div>{data.buildingname}</div>
        <div>
          {data.number} {data.street}
        </div>
        <div>{data.posttown}</div>
        <div>{data.postcode}</div>
        <div>{data.country}</div>

        <Button variant='tertiary' size='sm' onClick={() => handleViewChange('edit')}>
          Edit address
        </Button>
      </div>
    </div>
  )

  const editView = (
    <Fragment>
      <Button variant='tertiary' size='sm' onClick={() => handleViewChange('postcode')}>
        Back to address finder
      </Button>
      <div className='editView'>
        {addressEditFields.map((field, index) => {
          return (
            <div key={index}>
              <label htmlFor={field.name}>{field.label}</label>
              <Input
                type='text'
                name={`${name}.${field.name}`}
                onChange={(e) => returnData(field.name, e.currentTarget.value)}
                value={data[field.name]}
              />
              {errors?.[field.name] && (
                <div className='SMEform__error'>{errors?.[field.name]?.message}</div>
              )}
            </div>
          )
        })}
        <input type='hidden' value={data.uprn} />
      </div>
    </Fragment>
  )
  return (
    <div>
      {addressView === 'postcode'
        ? postcodeView
        : postcodeError || errors || addressView === 'edit' || editMode
        ? editView
        : staticView}
    </div>
  )
}

export default AddressFormComponent
